<template>
  <SfCardInline ui-state="default">
    <template #left>
      <SfEllipsis
        :length="documentNameEllipsisLength"
        :tooltip="document.name"
        >{{ document.name }}</SfEllipsis
      >
    </template>
    <template #right>
      <template v-if="isActivelyDownloading">
        <div class="tw-flex tw-rounded-full tw-h-2 tw-bg-gray-200 tw-w-[80px]">
          <div
            :style="{ width: progress + '%' }"
            class="tw-rounded-full tw-bg-sf-primary-light"
          ></div>
        </div>
        <div
          class="
            tw-flex tw-w-[30px] tw-text-[11px] tw-font-medium tw-justify-end
          "
        >
          {{ progress }}%
        </div>
      </template>
      <div class="tw-flex tw-items-center tw-gap-2 tw-text-sf-neutral">
        <SfEllipsis
          v-if="isDocument && uploadedByText"
          :length="documentUserEllipsisLength"
          :tooltip="uploadedByText"
          >{{ uploadedByText }}</SfEllipsis
        >
        <span
          v-if="isDocument && uploadedByText && document.size"
          class="tw-text-sf-neutral-light"
          >·</span
        >
        <SfEllipsis
          v-if="document.size"
          :length="documentSizeEllipsisLength"
          :tooltip="secondaryText"
          >{{ secondaryText }}</SfEllipsis
        >
        <div class="tw-flex tw-gap-[2px]">
          <i
            v-if="isDocument"
            class="
              sf-pointer
              fa fa-download
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            @click="handleDownload"
          ></i>
          <i
            v-if="isDocument"
            class="
              sf-pointer
              fa fa-trash
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-delete"
            @click="handleDelete"
          ></i>
        </div>
      </div>
    </template>
  </SfCardInline>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SfCardInline from '@app/shared/vue/components/sf-card/sf-card-inline.vue'
import SfEllipsis from '@app/shared/vue/components/sf-ellipsis/sf-ellipsis.vue'
import { formatBytes } from '@app/utils/file-helpers'
import { DocumentLinkType } from '@app/shared/types/sf-data'
import type { ExternalNoteUploaderItem } from '@app/application/client_meetings/types'

interface Props {
  document: ExternalNoteUploaderItem
  progress?: string
}

const props = withDefaults(defineProps<Props>(), {
  progress: undefined,
})

const emit =
  defineEmits<{
    delete: []
  }>()

const secondaryText = computed(() =>
  props.document.size ? formatBytes(props.document.size) : null
)

const uploadedByText = computed(() => {
  if (props.document.linkType !== DocumentLinkType.Document) return null
  return props.document.uploadedBy || null
})

const isActivelyDownloading = computed(() => {
  return props.progress !== undefined && props.progress !== '100'
})

const documentNameEllipsisLength = computed(() =>
  isActivelyDownloading.value ? 185 : 300
)

const documentSizeEllipsisLength = computed(() =>
  isActivelyDownloading.value ? 75 : 90
)

const documentUserEllipsisLength = computed(() =>
  isActivelyDownloading.value ? 100 : 150
)

const isDocument = computed(
  () => props.document.linkType === DocumentLinkType.Document
)

function handleDownload() {
  if (props.document.linkType !== DocumentLinkType.Document) {
    return
  }

  const url = window.Silverfin.Routes.client_meetings_external_note_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    props.document.document_id
  )

  window.open(url)
}

function handleDelete() {
  emit('delete')
}
</script>
